import React, { useState, useEffect } from 'react';
import {
  HeartPulse,
  Activity,
  Dumbbell,
  Apple,
  Moon,
  Pill,
  Flame,
  Sparkles,
  CheckCircle2,
  Droplets,
  RefreshCw,
  Send,
  Smartphone,
  Radio,
  DoorOpen,
  MessageSquareHeart,
  ShieldCheck,
  UserCheck
} from 'lucide-react';
import { Patient, AmbientRoom, FamilyDigestSubject } from '../types';
import { generatePreventiveWellnessCoachReply } from '../services/geminiService';

interface PreventiveWellnessSuiteProps {
  patient: Patient;
  patients?: Patient[];
  onSelectPatient?: (pt: Patient) => void;
  ambientRooms?: AmbientRoom[];
  onOpenFamilyDigest?: (subject: FamilyDigestSubject) => void;
  onSyncConsumerAdl?: (roomId: string, type: 'medication' | 'nutrition') => void;
}

interface MedicationReminderItem {
  id: string;
  name: string;
  dosage: string;
  timing: string;
  taken: boolean;
  sensorVerified?: boolean;
  notes: string;
}

interface HabitItem {
  id: string;
  title: string;
  cue: string;
  streak: number;
  completed: boolean;
}

interface SubjectWellnessProfile {
  id: string;
  roomId?: string;
  name: string;
  mrnOrRoom: string;
  age: number;
  diagnosis: string;
  targetSteps: number;
  initialSteps: number;
  waterLiters: number;
  targetWaterLiters: number;
  sleepTotal: string;
  sleepDeep: string;
  sleepRem: string;
  sleepEfficiency: number;
  fitnessTag: string;
  fitnessPlan1Title: string;
  fitnessPlan1Target: string;
  fitnessPlan1Desc: string;
  fitnessPlan2Title: string;
  fitnessPlan2Target: string;
  fitnessPlan2Desc: string;
  nutritionTag: string;
  nutritionTargetText: string;
  meds: MedicationReminderItem[];
  habits: HabitItem[];
}

const SUBJECT_PROFILES: Record<string, SubjectWellnessProfile> = {
  'room-101': {
    id: 'room-101',
    roomId: '101',
    name: 'Eleanor Vance',
    mrnOrRoom: 'Room 101 • Wing-A Rehab',
    age: 84,
    diagnosis: 'Post-Op Knee Arthroplasty & Osteoporosis',
    targetSteps: 4500,
    initialSteps: 3680,
    waterLiters: 1.9,
    targetWaterLiters: 2.5,
    sleepTotal: '7h 52m',
    sleepDeep: '1h 48m',
    sleepRem: '2h 05m',
    sleepEfficiency: 92,
    fitnessTag: 'Post-Op Gait • 0.94 m/s',
    fitnessPlan1Title: '15m Assisted Corridor Gait Walk',
    fitnessPlan1Target: '0.94 m/s Radar Verified',
    fitnessPlan1Desc: 'Quadriceps neuromuscular recruitment & knee extension range of motion.',
    fitnessPlan2Title: '10m Seated Ankle Pumps & Isometric Quad Sets',
    fitnessPlan2Target: 'DVT Prophylaxis',
    fitnessPlan2Desc: 'Venous return enhancement and joint edema reduction.',
    nutritionTag: 'High Calcium • Anti-Inflammatory',
    nutritionTargetText:
      'High-protein collagen & calcium bone-remodeling protocol (1,200mg Ca + Vit D3). Recommended dinner: Wild salmon, sautéed kale, quinoa, and turmeric-ginger broth.',
    meds: [
      {
        id: 'ev-1',
        name: 'Apixaban (Eliquis)',
        dosage: '2.5mg PO',
        timing: 'Morning (08:00 AM)',
        taken: true,
        sensorVerified: true,
        notes: 'Post-Op DVT Prophylaxis • Verified by Aqara Med Cabinet Sensor'
      },
      {
        id: 'ev-2',
        name: 'Calcium Citrate + Vit D3',
        dosage: '600mg / 1000 IU',
        timing: 'Morning with Breakfast',
        taken: true,
        sensorVerified: true,
        notes: 'Osteoporosis Bone Density Support • Taken at 8:20 AM'
      },
      {
        id: 'ev-3',
        name: 'Celecoxib',
        dosage: '100mg PO',
        timing: 'Afternoon (02:00 PM)',
        taken: false,
        notes: 'Targeted Joint Anti-Inflammatory • Before Afternoon PT'
      },
      {
        id: 'ev-4',
        name: 'Magnesium Glycinate',
        dosage: '300mg PO',
        timing: 'Bedtime (09:00 PM)',
        taken: false,
        notes: 'Nocturnal Muscle Relaxation & Deep Sleep Support'
      }
    ],
    habits: [
      { id: 'ev-h1', title: 'Morning Knee Extension Mobility (10m)', cue: 'Before first corridor walk', streak: 12, completed: true },
      { id: 'ev-h2', title: '2.5L Hydration & Electrolyte Goal', cue: '250ml water every 2 hours', streak: 16, completed: true },
      { id: 'ev-h3', title: 'Afternoon Physical Therapy Walk', cue: 'Tracked by 60GHz ceiling radar', streak: 9, completed: false },
      { id: 'ev-h4', title: 'Clear Nightstand & Low-Light Pathway', cue: 'Before 9:00 PM sleep window', streak: 21, completed: true }
    ]
  },
  'room-102': {
    id: 'room-102',
    roomId: '102',
    name: 'Julian Hayes',
    mrnOrRoom: 'Room 102 • Wing-A Memory & Mobility',
    age: 79,
    diagnosis: 'Early Parkinsonism & Orthostatic Hypotension',
    targetSteps: 5000,
    initialSteps: 3420,
    waterLiters: 1.6,
    targetWaterLiters: 2.5,
    sleepTotal: '6h 45m',
    sleepDeep: '1h 15m',
    sleepRem: '1h 40m',
    sleepEfficiency: 82,
    fitnessTag: 'Rhythmic Cueing • 0.72 m/s',
    fitnessPlan1Title: '20m Metronome-Cued Stride Training',
    fitnessPlan1Target: 'Cadence 100 steps/min',
    fitnessPlan1Desc: 'Prevents Parkinsonian freezing of gait and lengthens stride amplitude.',
    fitnessPlan2Title: '10m Sit-to-Stand Orthostatic Conditioning',
    fitnessPlan2Target: '30s Edge-of-Bed Pause',
    fitnessPlan2Desc: 'Stabilizes baroreceptor reflex before standing ambulation.',
    nutritionTag: 'Timed Protein • Orthostatic Hydration',
    nutritionTargetText:
      'Protein-redistribution diet (keep daytime snacks low-protein to maximize Levodopa absorption; concentrate protein at dinner) + 500ml morning bolus hydration.',
    meds: [
      {
        id: 'jh-1',
        name: 'Carbidopa-Levodopa (Sinemet)',
        dosage: '25/100mg PO',
        timing: 'Morning (07:30 AM - 30m pre-meal)',
        taken: true,
        sensorVerified: true,
        notes: 'Dopaminergic Motor Control • Verified by Med Cabinet Sensor'
      },
      {
        id: 'jh-2',
        name: 'Midodrine',
        dosage: '5mg PO',
        timing: 'Morning (08:00 AM)',
        taken: true,
        sensorVerified: true,
        notes: 'Orthostatic Blood Pressure Support • Taken with 16oz water'
      },
      {
        id: 'jh-3',
        name: 'Carbidopa-Levodopa (Dose 2)',
        dosage: '25/100mg PO',
        timing: 'Afternoon (01:30 PM)',
        taken: false,
        notes: 'Afternoon Gait & Tremor Stabilization'
      },
      {
        id: 'jh-4',
        name: 'Melatonin Sustained Release',
        dosage: '3mg PO',
        timing: 'Bedtime (08:30 PM)',
        taken: false,
        notes: 'REM Sleep Behavior & Circadian Alignment'
      }
    ],
    habits: [
      { id: 'jh-h1', title: '16oz Morning Water Bolus Before Standing', cue: 'Upon waking at edge of bed', streak: 11, completed: true },
      { id: 'jh-h2', title: '30-Second Edge-of-Bed Pause', cue: 'Monitored by bed presence timer', streak: 18, completed: true },
      { id: 'jh-h3', title: '15m High-Step Rhythmic Walk', cue: 'Mid-morning in sunlit hallway', streak: 6, completed: false },
      { id: 'jh-h4', title: 'Evening Fluid Taper (Reduce Nocturia)', cue: '90m before bedtime', streak: 9, completed: false }
    ]
  },
  'room-103': {
    id: 'room-103',
    roomId: '103',
    name: 'Robert Chen',
    mrnOrRoom: 'Room 103 • Wing-B Cardiopulmonary',
    age: 88,
    diagnosis: 'CHF Stage II & Nocturnal Frequency Risk',
    targetSteps: 4000,
    initialSteps: 2890,
    waterLiters: 1.5,
    targetWaterLiters: 1.8,
    sleepTotal: '7h 10m',
    sleepDeep: '1h 30m',
    sleepRem: '1h 55m',
    sleepEfficiency: 85,
    fitnessTag: 'Cardiac Rehab • Zone 1-2',
    fitnessPlan1Title: '20m Paced Level-Ground Cardiac Walk',
    fitnessPlan1Target: 'HR 95-110 BPM • SpO2 >95%',
    fitnessPlan1Desc: 'Supports left-ventricular stroke volume without fluid-overload dyspnea.',
    fitnessPlan2Title: '10m Inspiratory Muscle & Diaphragmatic Breathing',
    fitnessPlan2Target: '6 breaths/min vagal tone',
    fitnessPlan2Desc: 'Enhances heart-rate variability (HRV) and pulmonary gas exchange.',
    nutritionTag: 'Strict <1,500mg Sodium • Fluid Balance',
    nutritionTargetText:
      'Cardiorenal DASH protocol: Strict <1,500mg sodium/day and 1.8L fluid ceiling to prevent +3.0 lb 48h fluid retention spikes. Herb-roasted cod, steamed broccoli, and lemon-olive oil.',
    meds: [
      {
        id: 'rc-1',
        name: 'Furosemide (Lasix)',
        dosage: '40mg PO',
        timing: 'Morning (08:00 AM)',
        taken: false,
        sensorVerified: false,
        notes: 'Loop Diuretic • Take early to prevent nocturnal bathroom trips'
      },
      {
        id: 'rc-2',
        name: 'Sacubitril/Valsartan (Entresto)',
        dosage: '49/51mg PO',
        timing: 'Morning (08:30 AM)',
        taken: false,
        sensorVerified: false,
        notes: 'CHF Neurohormonal Cardioprotection'
      },
      {
        id: 'rc-3',
        name: 'Spironolactone',
        dosage: '25mg PO',
        timing: 'Midday with Lunch (12:30 PM)',
        taken: false,
        notes: 'Potassium-Sparing Aldosterone Antagonist'
      },
      {
        id: 'rc-4',
        name: 'Metoprolol Succinate ER',
        dosage: '50mg PO',
        timing: 'Evening (06:00 PM)',
        taken: true,
        sensorVerified: true,
        notes: 'Rate & Ventricular Remodeling Control'
      }
    ],
    habits: [
      { id: 'rc-h1', title: 'Morning Cellular Weight Scale Check', cue: 'Immediately after first morning void', streak: 24, completed: true },
      { id: 'rc-h2', title: 'Morning Cellular Blood Pressure Check', cue: 'Seated quietly for 5m prior', streak: 19, completed: true },
      { id: 'rc-h3', title: 'Take Diuretic Before 9:00 AM', cue: 'Prevents nighttime bathroom/UTI spikes', streak: 8, completed: false },
      { id: 'rc-h4', title: 'Low-Sodium Meal Verification', cue: 'Tracked via Fridge Contact Sensor', streak: 15, completed: false }
    ]
  }
};

function buildProfileForPatient(patient: Patient): SubjectWellnessProfile {
  return {
    id: patient.id,
    name: patient.fullName,
    mrnOrRoom: `MRN: ${patient.mrn} • ${patient.department}`,
    age: patient.age,
    diagnosis: patient.primaryDiagnosis,
    targetSteps: 10000,
    initialSteps: 8420,
    waterLiters: 2.1,
    targetWaterLiters: 3.0,
    sleepTotal: '7h 48m',
    sleepDeep: '1h 45m',
    sleepRem: '2h 10m',
    sleepEfficiency: 91,
    fitnessTag: 'Zone-2 Cardio • Low Impact',
    fitnessPlan1Title: '30m Aerobic Zone-2 Walking',
    fitnessPlan1Target: '115-125 BPM',
    fitnessPlan1Desc: 'Mitochondrial biogenesis & vascular endothelial compliance.',
    fitnessPlan2Title: '10m Postural Spine Mobility',
    fitnessPlan2Target: 'Thoracic Decompression',
    fitnessPlan2Desc: 'Flexibility and diaphragmatic breathing decompression.',
    nutritionTag: 'Low Sodium • Mediterranean',
    nutritionTargetText: `Under 1,500mg sodium daily for ${patient.primaryDiagnosis}. Recommended dinner: Wild salmon, steamed asparagus, extra-virgin olive oil, and brown rice (high Omega-3 & potassium).`,
    meds: [
      {
        id: 'm1',
        name: 'Lisinopril',
        dosage: '20mg PO',
        timing: 'Morning (08:00 AM)',
        taken: true,
        sensorVerified: true,
        notes: 'Blood Pressure Optimization • Taken at 8:05 AM'
      },
      {
        id: 'm2',
        name: 'Furosemide (Lasix)',
        dosage: '40mg PO',
        timing: 'Morning with food',
        taken: true,
        sensorVerified: true,
        notes: 'Diuretic • Taken at 8:15 AM'
      },
      {
        id: 'm3',
        name: 'Metoprolol Succinate',
        dosage: '25mg PO',
        timing: 'Evening with Dinner',
        taken: false,
        notes: 'Cardioprotection • Scheduled for 6:30 PM'
      },
      {
        id: 'm4',
        name: 'Magnesium Glycinate',
        dosage: '300mg',
        timing: 'Bedtime (60m prior)',
        taken: false,
        notes: 'Sleep Architecture & Muscle Recovery • 9:30 PM'
      }
    ],
    habits: [
      { id: 'h1', title: 'Morning Sunlight (15 mins)', cue: 'Within 30m of waking', streak: 14, completed: true },
      { id: 'h2', title: '2.5L Hydration Goal', cue: 'Drink 500ml before meals', streak: 9, completed: true },
      { id: 'h3', title: '10m Post-Meal Walk', cue: 'After lunch & dinner', streak: 7, completed: false },
      { id: 'h4', title: 'Digital Sunset (No screens 45m before bed)', cue: 'After 9:30 PM', streak: 21, completed: false }
    ]
  };
}

export const PreventiveWellnessSuite: React.FC<PreventiveWellnessSuiteProps> = ({
  patient,
  patients = [],
  onSelectPatient,
  ambientRooms = [],
  onOpenFamilyDigest,
  onSyncConsumerAdl
}) => {
  const [selectedSubjectKey, setSelectedSubjectKey] = useState<string>('room-101');
  const [activeSubTab, setActiveSubTab] = useState<'overview' | 'fitness' | 'nutrition' | 'sleep' | 'medications' | 'habits'>('overview');
  const [showConsumerMobilePreview, setShowConsumerMobilePreview] = useState<boolean>(true);
  const [syncStatus, setSyncStatus] = useState<string>('Health Connect + Aqara IoT: Synced');
  const [isSyncing, setIsSyncing] = useState<boolean>(false);

  const baseProfile = SUBJECT_PROFILES[selectedSubjectKey] || buildProfileForPatient(patient);
  const linkedRoom = baseProfile.roomId
    ? ambientRooms.find((r) => r.roomId === baseProfile.roomId)
    : undefined;

  const [steps, setSteps] = useState<number>(baseProfile.initialSteps);
  const [waterLiters, setWaterLiters] = useState<number>(baseProfile.waterLiters);
  const [meds, setMeds] = useState<MedicationReminderItem[]>(baseProfile.meds);
  const [habits, setHabits] = useState<HabitItem[]>(baseProfile.habits);
  const [healthScore, setHealthScore] = useState<number>(88);

  // Sync profile state when user switches resident/patient
  useEffect(() => {
    const prof = SUBJECT_PROFILES[selectedSubjectKey] || buildProfileForPatient(patient);
    setSteps(prof.initialSteps);
    setWaterLiters(prof.waterLiters);
    setMeds(prof.meds);
    setHabits(prof.habits);
    setChatMessages([
      {
        sender: 'ai',
        text: `Welcome to the Preventive Longevity & Consumer App Hub for ${prof.name} (${prof.mrnOrRoom}). Google Health Connect & Aqara Ambient Sensors show ${prof.initialSteps.toLocaleString()} steps today and ${prof.sleepTotal} of sleep (${prof.sleepDeep} deep). How can I help optimize ${prof.name.split(' ')[0]}'s recovery today?`
      }
    ]);
  }, [selectedSubjectKey, patient.id]);

  // Automatically reflect live Aqara Med Cabinet & Fridge Contact Sensor events from Tab 2!
  useEffect(() => {
    if (!linkedRoom) return;
    if (linkedRoom.adlActivity.medCabinetOpened) {
      setMeds((prev) =>
        prev.map((m, idx) =>
          idx < 2 ? { ...m, taken: true, sensorVerified: true } : m
        )
      );
    }
    if (linkedRoom.adlActivity.fridgeOpened) {
      setHabits((prev) =>
        prev.map((h) =>
          h.title.toLowerCase().includes('meal') || h.title.toLowerCase().includes('hydration')
            ? { ...h, completed: true }
            : h
        )
      );
    }
  }, [linkedRoom?.adlActivity.medCabinetOpened, linkedRoom?.adlActivity.fridgeOpened]);

  // Recalculate Preventive Wellness Index whenever meds or habits change
  useEffect(() => {
    const takenMeds = meds.filter((m) => m.taken).length;
    const doneHabits = habits.filter((h) => h.completed).length;
    const stepRatio = Math.min(1, steps / baseProfile.targetSteps);
    const computed = Math.round(
      (takenMeds / Math.max(1, meds.length)) * 40 +
      (doneHabits / Math.max(1, habits.length)) * 35 +
      stepRatio * 25
    );
    setHealthScore(Math.min(100, computed));
  }, [meds, habits, steps, baseProfile.targetSteps]);

  // Coach Chat Messages
  const [chatMessages, setChatMessages] = useState<Array<{ sender: 'ai' | 'user'; text: string }>>([
    {
      sender: 'ai',
      text: `Welcome to the Preventive Longevity & Consumer App Hub for ${baseProfile.name} (${baseProfile.mrnOrRoom}). Google Health Connect & Aqara Ambient Sensors show ${baseProfile.initialSteps.toLocaleString()} steps today and ${baseProfile.sleepTotal} of sleep (${baseProfile.sleepDeep} deep). How can I help optimize ${baseProfile.name.split(' ')[0]}'s recovery today?`
    }
  ]);
  const [chatInput, setChatInput] = useState<string>('');
  const [isThinking, setIsThinking] = useState<boolean>(false);

  // Sync with Google Health Connect + Cloud Sensor Webhook
  const handleSyncHealthConnect = async () => {
    setIsSyncing(true);
    setSyncStatus('Syncing Wear OS / Health Connect + Aqara Hub...');
    const added = Math.floor(Math.random() * 280) + 120;
    const nextSteps = steps + added;
    setSteps(nextSteps);

    if (baseProfile.roomId) {
      try {
        await fetch(
          'https://us-central1-rugged-alcove-388517.cloudfunctions.net/aqaraRadarWebhook?key=mc-radar-sec-7f9a2b4e8c1d3e5f',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              roomNumber: baseProfile.roomId,
              event: 'heartbeat',
              deviceId: `health-connect-room${baseProfile.roomId}`,
              deviceType: 'Consumer App / Health Connect Wearable'
            })
          }
        );
      } catch {
        // ignore offline
      }
    }

    setTimeout(() => {
      setIsSyncing(false);
      setSyncStatus('Health Connect + Aqara IoT: Synced Just Now');
    }, 700);
  };

  const toggleMed = (id: string) => {
    setMeds((prev) =>
      prev.map((m) =>
        m.id === id ? { ...m, taken: !m.taken, sensorVerified: !m.taken } : m
      )
    );
    if (baseProfile.roomId && onSyncConsumerAdl) {
      onSyncConsumerAdl(baseProfile.roomId, 'medication');
    }
  };

  const toggleHabit = (id: string) => {
    setHabits((prev) =>
      prev.map((h) =>
        h.id === id
          ? { ...h, completed: !h.completed, streak: !h.completed ? h.streak + 1 : Math.max(1, h.streak - 1) }
          : h
      )
    );
  };

  const handleLogWaterAndNutrition = () => {
    setWaterLiters((prev) => +(prev + 0.25).toFixed(2));
    if (baseProfile.roomId && onSyncConsumerAdl) {
      onSyncConsumerAdl(baseProfile.roomId, 'nutrition');
    }
  };

  const handleSendMessage = async (textToSend?: string) => {
    const query = textToSend || chatInput.trim();
    if (!query) return;

    setChatMessages((prev) => [...prev, { sender: 'user', text: query }]);
    if (!textToSend) setChatInput('');
    setIsThinking(true);

    const adlStatus = linkedRoom
      ? `Fridge Sensor: ${linkedRoom.adlActivity.fridgeOpened ? 'Opened (' + linkedRoom.adlActivity.fridgeLastOpened + ')' : 'Not opened yet'}, Med Cabinet Sensor: ${linkedRoom.adlActivity.medCabinetOpened ? 'Opened (' + linkedRoom.adlActivity.medCabinetLastOpened + ')' : 'Pending'}, Cellular BP: ${linkedRoom.rpmVitals.cellularBP}`
      : 'Wearable & FCM reminders active';

    const reply = await generatePreventiveWellnessCoachReply({
      subjectName: baseProfile.name,
      age: baseProfile.age,
      diagnosis: baseProfile.diagnosis,
      steps,
      targetSteps: baseProfile.targetSteps,
      sleepSummary: `${baseProfile.sleepTotal} total (${baseProfile.sleepDeep} deep, ${baseProfile.sleepRem} REM, ${baseProfile.sleepEfficiency}% efficiency)`,
      waterLiters,
      medications: meds.map((m) => `${m.name} ${m.dosage} (${m.taken ? 'Taken' : 'Pending'})`),
      adlSensorStatus: adlStatus,
      userQuery: query
    });

    setIsThinking(false);
    setChatMessages((prev) => [...prev, { sender: 'ai', text: reply }]);
  };

  return (
    <div className="space-y-6 animate-fadeIn pb-12">
      {/* Top Banner, Resident/Patient Switcher & Consumer App Companion Toggle */}
      <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5 backdrop-blur-md shadow-xl flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        <div className="flex items-start sm:items-center gap-3">
          <div className="p-3 bg-teal-500/10 border border-teal-500/20 text-teal-400 rounded-xl shrink-0">
            <HeartPulse className="w-6 h-6" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg sm:text-xl font-bold text-slate-100">
                Preventive Health &amp; Consumer Wellness Bridge
              </h1>
              <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-teal-500/20 text-teal-300 border border-teal-500/30">
                Google Health Connect + Aqara ADL Sync
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-0.5">
              Active Profile: <strong className="text-slate-200">{baseProfile.name}</strong> ({baseProfile.mrnOrRoom}) • Primary: <span className="text-teal-300">{baseProfile.diagnosis}</span>
            </p>
          </div>
        </div>

        {/* Controls: Subject Selector + Consumer App View Toggle + Sync */}
        <div className="flex flex-wrap items-center gap-2.5 w-full lg:w-auto justify-end">
          <div className="flex items-center gap-1.5 bg-slate-950/90 border border-slate-700 rounded-xl px-2.5 py-1.5">
            <UserCheck className="w-3.5 h-3.5 text-teal-400 shrink-0" />
            <select
              value={selectedSubjectKey}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedSubjectKey(val);
                if (!val.startsWith('room-') && onSelectPatient) {
                  const found = patients.find((p) => p.id === val);
                  if (found) onSelectPatient(found);
                }
              }}
              className="bg-transparent text-xs font-semibold text-slate-200 focus:outline-none cursor-pointer"
            >
              <optgroup label="Ambient Senior Living & Home RPM Residents" className="bg-slate-900 text-teal-300">
                <option value="room-101">Room 101 — Eleanor Vance (84yo • Post-Op Knee)</option>
                <option value="room-102">Room 102 — Julian Hayes (79yo • Parkinsonism)</option>
                <option value="room-103">Room 103 — Robert Chen (88yo • CHF Stage II)</option>
              </optgroup>
              {patients.length > 0 && (
                <optgroup label="Inpatient Acute Census" className="bg-slate-900 text-sky-300">
                  {patients.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.fullName} ({p.mrn} • {p.primaryDiagnosis})
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

          <button
            onClick={() => setShowConsumerMobilePreview((prev) => !prev)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border transition ${
              showConsumerMobilePreview
                ? 'bg-teal-500/20 border-teal-500/50 text-teal-300 shadow-sm'
                : 'bg-slate-800/80 border-slate-700 text-slate-300 hover:text-white'
            }`}
          >
            <Smartphone className="w-3.5 h-3.5" />
            <span>{showConsumerMobilePreview ? 'Consumer App Preview: ON' : 'Show Consumer App View'}</span>
          </button>

          <button
            onClick={handleSyncHealthConnect}
            disabled={isSyncing}
            className="flex items-center gap-2 px-3 py-2 bg-slate-800/80 hover:bg-slate-800 border border-slate-700 rounded-xl text-xs font-medium text-slate-200 transition"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-teal-400 ${isSyncing ? 'animate-spin' : ''}`} />
            <span>{syncStatus}</span>
          </button>
        </div>
      </div>

      {/* Live Ambient Sensor <-> Consumer App Alignment Bar */}
      {linkedRoom && (
        <div className="bg-slate-950/90 border border-teal-500/30 rounded-2xl p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-teal-500/15 border border-teal-500/30 text-teal-300">
              <Radio className="w-4 h-4 animate-pulse" />
            </div>
            <div>
              <div className="text-xs font-bold text-white flex items-center gap-2">
                <span>Bi-Directional Consumer App &amp; Ambient IoT Sensor Sync ({linkedRoom.roomNumber})</span>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                  LIVE BRIDGE
                </span>
              </div>
              <p className="text-[11px] text-slate-400">
                When {linkedRoom.residentName} opens the pill cabinet or fridge—or checks off a dose on their phone—both the Consumer App and Nurse Station update together.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono">
            <span className="px-2.5 py-1 rounded-lg bg-slate-900 border border-slate-800 text-cyan-300 flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5 text-cyan-400" />
              <span>Radar Gait: {linkedRoom.gaitSpeedMs} m/s</span>
            </span>
            <span
              className={`px-2.5 py-1 rounded-lg border flex items-center gap-1.5 ${
                linkedRoom.adlActivity.medCabinetOpened
                  ? 'bg-emerald-950/40 border-emerald-500/40 text-emerald-300'
                  : 'bg-amber-950/40 border-amber-500/40 text-amber-300'
              }`}
            >
              <Pill className="w-3.5 h-3.5" />
              <span>
                Med Sensor: {linkedRoom.adlActivity.medCabinetOpened ? `Verified (${linkedRoom.adlActivity.medCabinetLastOpened})` : 'Awaiting Dose'}
              </span>
            </span>
            <span
              className={`px-2.5 py-1 rounded-lg border flex items-center gap-1.5 ${
                linkedRoom.adlActivity.fridgeOpened
                  ? 'bg-emerald-950/40 border-emerald-500/40 text-emerald-300'
                  : 'bg-amber-950/40 border-amber-500/40 text-amber-300'
              }`}
            >
              <DoorOpen className="w-3.5 h-3.5" />
              <span>
                Fridge Sensor: {linkedRoom.adlActivity.fridgeOpened ? `Opened (${linkedRoom.adlActivity.fridgeLastOpened})` : 'No Meal Logged'}
              </span>
            </span>
          </div>
        </div>
      )}

      {/* Hero Preventive Score Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-teal-900/40 via-slate-900/80 to-slate-950 border border-teal-500/30 rounded-2xl p-5 shadow-lg flex items-center justify-between col-span-1 md:col-span-2">
          <div>
            <span className="text-xs font-bold text-teal-400 uppercase tracking-wider">Preventive Wellness Index</span>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-4xl font-extrabold text-white tracking-tight">{healthScore}</span>
              <span className="text-slate-400 text-sm font-medium">/ 100</span>
            </div>
            <p className="text-xs text-teal-200/80 mt-1 max-w-md">
              Combines wearable activity ({Math.round((steps / baseProfile.targetSteps) * 100)}% of step goal), sleep recovery ({baseProfile.sleepEfficiency}%), and sensor-verified medication adherence ({meds.filter((m) => m.taken).length}/{meds.length} doses).
            </p>
          </div>
          <div className="w-16 h-16 rounded-full border-4 border-teal-400/40 border-t-teal-400 flex items-center justify-center font-bold text-teal-300 text-lg shadow-inner shrink-0">
            {healthScore}%
          </div>
        </div>

        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5 shadow-lg">
          <div className="flex items-center justify-between text-slate-400 text-xs font-medium mb-1">
            <span>Daily Activity (Steps)</span>
            <Activity className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="text-2xl font-bold text-slate-100">{steps.toLocaleString()}</div>
          <div className="w-full bg-slate-800 h-1.5 rounded-full mt-2 overflow-hidden">
            <div
              className="bg-emerald-400 h-full rounded-full transition-all"
              style={{ width: `${Math.min(100, (steps / baseProfile.targetSteps) * 100)}%` }}
            />
          </div>
          <div className="flex justify-between text-[11px] text-slate-400 mt-1">
            <span>Target: {baseProfile.targetSteps.toLocaleString()}</span>
            <span className="text-emerald-400 font-semibold">
              {Math.round((steps / baseProfile.targetSteps) * 100)}%
            </span>
          </div>
        </div>

        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5 shadow-lg">
          <div className="flex items-center justify-between text-slate-400 text-xs font-medium mb-1">
            <span>Sleep Architecture</span>
            <Moon className="w-4 h-4 text-purple-400" />
          </div>
          <div className="text-2xl font-bold text-slate-100">{baseProfile.sleepTotal}</div>
          <div className="flex items-center gap-2 text-xs text-purple-300/90 mt-2">
            <span>
              Deep: <strong>{baseProfile.sleepDeep}</strong>
            </span>
            <span>•</span>
            <span>
              REM: <strong>{baseProfile.sleepRem}</strong>
            </span>
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">
            Circadian Efficiency: {baseProfile.sleepEfficiency}%
          </div>
        </div>
      </div>

      {/* Main Grid: Left Pillars + Optional Consumer Mobile Companion + Right Gemini Coach */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: The 5 Core Pillars */}
        <div className={`${showConsumerMobilePreview ? 'lg:col-span-5' : 'lg:col-span-8'} space-y-5`}>
          {/* Pillar Tabs Bar */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 border-b border-slate-800">
            <button
              onClick={() => setActiveSubTab('overview')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition shrink-0 ${
                activeSubTab === 'overview'
                  ? 'bg-teal-500/20 text-teal-300 border border-teal-500/30'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              All 5 Pillars
            </button>
            <button
              onClick={() => setActiveSubTab('fitness')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition shrink-0 ${
                activeSubTab === 'fitness'
                  ? 'bg-teal-500/20 text-teal-300 border border-teal-500/30'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              🏋️ Fitness
            </button>
            <button
              onClick={() => setActiveSubTab('nutrition')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition shrink-0 ${
                activeSubTab === 'nutrition'
                  ? 'bg-teal-500/20 text-teal-300 border border-teal-500/30'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              🥗 Nutrition
            </button>
            <button
              onClick={() => setActiveSubTab('sleep')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition shrink-0 ${
                activeSubTab === 'sleep'
                  ? 'bg-teal-500/20 text-teal-300 border border-teal-500/30'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              😴 Sleep
            </button>
            <button
              onClick={() => setActiveSubTab('medications')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition shrink-0 ${
                activeSubTab === 'medications'
                  ? 'bg-teal-500/20 text-teal-300 border border-teal-500/30'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              💊 Medications
            </button>
            <button
              onClick={() => setActiveSubTab('habits')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition shrink-0 ${
                activeSubTab === 'habits'
                  ? 'bg-teal-500/20 text-teal-300 border border-teal-500/30'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              📈 Habits
            </button>
          </div>

          {/* Pillar 1: Fitness Plans */}
          {(activeSubTab === 'overview' || activeSubTab === 'fitness') && (
            <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5 shadow-md">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-blue-500/10 text-blue-400 rounded-lg">
                    <Dumbbell className="w-4 h-4" />
                  </div>
                  <h3 className="text-sm font-bold text-slate-100">Personalized Mobility &amp; Longevity Plan</h3>
                </div>
                <span className="text-[11px] font-semibold text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-full border border-blue-500/20">
                  {baseProfile.fitnessTag}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-slate-800/60 p-3 rounded-xl border border-slate-700/50">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-200">{baseProfile.fitnessPlan1Title}</span>
                    <span className="text-[10px] text-teal-400 font-semibold">{baseProfile.fitnessPlan1Target}</span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">{baseProfile.fitnessPlan1Desc}</p>
                </div>
                <div className="bg-slate-800/60 p-3 rounded-xl border border-slate-700/50">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-200">{baseProfile.fitnessPlan2Title}</span>
                    <span className="text-[10px] text-purple-400 font-semibold">{baseProfile.fitnessPlan2Target}</span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">{baseProfile.fitnessPlan2Desc}</p>
                </div>
              </div>
            </div>
          )}

          {/* Pillar 2: Nutrition Recommendations */}
          {(activeSubTab === 'overview' || activeSubTab === 'nutrition') && (
            <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5 shadow-md">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-emerald-500/10 text-emerald-400 rounded-lg">
                    <Apple className="w-4 h-4" />
                  </div>
                  <h3 className="text-sm font-bold text-slate-100">Anti-Inflammatory Nutrition &amp; Hydration</h3>
                </div>
                <span className="text-[11px] font-semibold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
                  {baseProfile.nutritionTag}
                </span>
              </div>
              <div className="bg-slate-800/50 p-3 rounded-xl border border-slate-700/50 text-xs text-slate-300 mb-3 leading-relaxed">
                <strong className="text-slate-100">Clinical Dietary Protocol:</strong> {baseProfile.nutritionTargetText}
              </div>
              <div className="flex items-center justify-between text-xs text-slate-300 bg-slate-800/30 p-2.5 rounded-lg border border-slate-800">
                <span className="flex items-center gap-1.5">
                  <Droplets className="w-3.5 h-3.5 text-cyan-400" />
                  <span>
                    Hydration: <strong>{waterLiters}L / {baseProfile.targetWaterLiters}L</strong>
                  </span>
                </span>
                <button
                  onClick={handleLogWaterAndNutrition}
                  className="px-2.5 py-1 bg-teal-500/20 hover:bg-teal-500/30 text-teal-300 font-semibold rounded text-[11px] border border-teal-500/30 transition"
                >
                  +250ml Water (Sync ADL)
                </button>
              </div>
            </div>
          )}

          {/* Pillar 3: Sleep Improvement */}
          {(activeSubTab === 'overview' || activeSubTab === 'sleep') && (
            <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5 shadow-md">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-purple-500/10 text-purple-400 rounded-lg">
                    <Moon className="w-4 h-4" />
                  </div>
                  <h3 className="text-sm font-bold text-slate-100">Circadian Rhythm &amp; Nocturnal Safety</h3>
                </div>
                <span className="text-[11px] font-semibold text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded-full border border-purple-500/20">
                  Efficiency {baseProfile.sleepEfficiency}%
                </span>
              </div>
              <div className="bg-purple-950/20 border border-purple-500/20 p-3 rounded-xl text-xs text-purple-200 leading-relaxed">
                🌙 <strong>Circadian &amp; Bed-Boundary Protocol:</strong> Cease caffeine by 2:00 PM; taper heavy fluids 90m before sleep to minimize nocturnal bathroom trips; 60GHz radar &amp; bed-presence timer monitor nighttime exits automatically.
              </div>
            </div>
          )}

          {/* Pillar 4: Medication Reminders */}
          {(activeSubTab === 'overview' || activeSubTab === 'medications') && (
            <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5 shadow-md">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-rose-500/10 text-rose-400 rounded-lg">
                    <Pill className="w-4 h-4" />
                  </div>
                  <h3 className="text-sm font-bold text-slate-100">Medication Reminders &amp; Pillbox Sensor Sync</h3>
                </div>
                <span className="text-[11px] font-semibold text-rose-400 bg-rose-500/10 px-2 py-0.5 rounded-full border border-rose-500/20">
                  FCM Push + Aqara Contact
                </span>
              </div>
              <div className="space-y-2">
                {meds.map((m) => (
                  <div
                    key={m.id}
                    onClick={() => toggleMed(m.id)}
                    className={`flex items-center justify-between p-3 rounded-xl border transition cursor-pointer ${
                      m.taken
                        ? 'bg-emerald-950/20 border-emerald-500/30'
                        : 'bg-slate-800/50 border-slate-700/60 hover:bg-slate-800/80'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-5 h-5 rounded-md flex items-center justify-center border text-xs shrink-0 ${
                          m.taken
                            ? 'bg-emerald-500 border-emerald-400 text-slate-950 font-bold'
                            : 'border-slate-600'
                        }`}
                      >
                        {m.taken ? '✓' : ''}
                      </div>
                      <div>
                        <div className="text-xs font-bold text-slate-100 flex items-center gap-2">
                          <span>
                            {m.name} {m.dosage}
                          </span>
                          {m.sensorVerified && m.taken && (
                            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-teal-500/20 text-teal-300 border border-teal-500/30">
                              Sensor Verified
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-400">
                          {m.timing} • {m.notes}
                        </div>
                      </div>
                    </div>
                    <span
                      className={`text-[11px] font-bold shrink-0 ml-2 ${
                        m.taken ? 'text-emerald-400' : 'text-amber-300'
                      }`}
                    >
                      {m.taken ? 'Taken' : 'Log Dose'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pillar 5: Habit Tracking */}
          {(activeSubTab === 'overview' || activeSubTab === 'habits') && (
            <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5 shadow-md">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-amber-500/10 text-amber-400 rounded-lg">
                    <Flame className="w-4 h-4" />
                  </div>
                  <h3 className="text-sm font-bold text-slate-100">Micro-Habit Stacking &amp; Streaks</h3>
                </div>
                <span className="text-[11px] font-semibold text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full border border-amber-500/20">
                  Atomic Habits Model
                </span>
              </div>
              <div className="space-y-2">
                {habits.map((h) => (
                  <div
                    key={h.id}
                    onClick={() => toggleHabit(h.id)}
                    className={`flex items-center justify-between p-3 rounded-xl border transition cursor-pointer ${
                      h.completed
                        ? 'bg-emerald-950/20 border-emerald-500/30'
                        : 'bg-slate-800/50 border-slate-700/60 hover:bg-slate-800/80'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-5 h-5 rounded-md flex items-center justify-center border text-xs shrink-0 ${
                          h.completed
                            ? 'bg-emerald-500 border-emerald-400 text-slate-950 font-bold'
                            : 'border-slate-600'
                        }`}
                      >
                        {h.completed ? '✓' : ''}
                      </div>
                      <div>
                        <div className="text-xs font-bold text-slate-100">{h.title}</div>
                        <div className="text-[11px] text-slate-400">{h.cue}</div>
                      </div>
                    </div>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/20 shrink-0">
                      🔥 {h.streak}d streak
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Center Column (When Enabled): Consumer Mobile App Companion Live Preview */}
        {showConsumerMobilePreview && (
          <div className="lg:col-span-3 flex flex-col items-center">
            <div className="w-full max-w-[320px] bg-slate-950 border-4 border-slate-800 rounded-[36px] p-4 shadow-2xl flex flex-col justify-between space-y-3 relative overflow-hidden">
              {/* Phone Notch Header */}
              <div className="flex items-center justify-between text-[10px] font-mono text-slate-400 pb-2 border-b border-slate-800/80">
                <span className="flex items-center gap-1 text-teal-300 font-bold">
                  <Smartphone className="w-3 h-3" />
                  <span>CareMeet Consumer App</span>
                </span>
                <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">LIVE SYNC</span>
              </div>

              {/* Resident Greeting & Ring */}
              <div className="p-3 rounded-2xl bg-gradient-to-br from-teal-950/80 to-slate-900 border border-teal-500/30 flex items-center justify-between">
                <div>
                  <span className="text-[10px] uppercase font-mono text-teal-300 block">My Daily Wellness</span>
                  <h4 className="text-sm font-extrabold text-white">{baseProfile.name.split(' ')[0]}'s App</h4>
                  <span className="text-[10px] text-slate-300">
                    {steps.toLocaleString()} steps • {waterLiters}L water
                  </span>
                </div>
                <div className="h-12 w-12 rounded-full border-2 border-teal-400 flex items-center justify-center font-black text-teal-300 text-xs">
                  {healthScore}%
                </div>
              </div>

              {/* Quick Consumer Actions that sync to Nurse Dashboard */}
              <div className="space-y-2">
                <span className="text-[10px] font-mono uppercase text-slate-400 block">
                  Tap on Phone &rarr; Syncs to Clinical Hub:
                </span>

                {meds.slice(0, 3).map((m) => (
                  <button
                    key={m.id}
                    onClick={() => toggleMed(m.id)}
                    className={`w-full p-2.5 rounded-xl border text-left transition flex items-center justify-between ${
                      m.taken
                        ? 'bg-emerald-950/30 border-emerald-500/40 text-emerald-200'
                        : 'bg-slate-900 border-slate-800 text-slate-200 hover:border-teal-500/40'
                    }`}
                  >
                    <div className="pr-2">
                      <div className="text-[11px] font-bold">{m.name}</div>
                      <div className="text-[9.5px] text-slate-400">{m.timing}</div>
                    </div>
                    <span
                      className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded ${
                        m.taken ? 'bg-emerald-500 text-slate-950' : 'bg-slate-800 text-amber-300'
                      }`}
                    >
                      {m.taken ? '✓ Taken' : 'Tap Taken'}
                    </span>
                  </button>
                ))}

                <button
                  onClick={handleLogWaterAndNutrition}
                  className="w-full p-2.5 rounded-xl bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-500/40 text-cyan-200 text-xs font-bold transition flex items-center justify-between"
                >
                  <span className="flex items-center gap-1.5">
                    <Droplets className="w-3.5 h-3.5 text-cyan-400" />
                    <span>Log Glass of Water (+250ml)</span>
                  </span>
                  <span className="text-[10px] font-mono">{waterLiters}L</span>
                </button>

                <button
                  onClick={handleSyncHealthConnect}
                  className="w-full p-2.5 rounded-xl bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 text-emerald-200 text-xs font-bold transition flex items-center justify-between"
                >
                  <span className="flex items-center gap-1.5">
                    <Activity className="w-3.5 h-3.5 text-emerald-400" />
                    <span>Sync Wearable Steps</span>
                  </span>
                  <span className="text-[10px] font-mono">+Steps</span>
                </button>
              </div>

              {/* Family SMS Digest Trigger from Consumer App */}
              {onOpenFamilyDigest && (
                <button
                  onClick={() => {
                    if (linkedRoom) {
                      onOpenFamilyDigest({
                        id: linkedRoom.roomId,
                        name: linkedRoom.residentName,
                        preferredName: linkedRoom.residentName.split(' ')[0],
                        recipientName: linkedRoom.familyContact.name,
                        recipientRelation: linkedRoom.familyContact.relation,
                        recipientPhone: linkedRoom.familyContact.phone,
                        locationOrRoom: linkedRoom.roomNumber,
                        sleepScoreOrState: `${baseProfile.sleepTotal} (${baseProfile.sleepEfficiency}% efficiency)`,
                        vitalsSummary: `BP ${linkedRoom.rpmVitals.cellularBP}, HR ${linkedRoom.rpmVitals.pulseBpm} bpm, Wellness Score ${healthScore}/100`,
                        nutritionActivitySummary: `Hydration ${waterLiters}L, ${steps.toLocaleString()} steps, ${meds.filter((m) => m.taken).length}/${meds.length} meds taken`
                      });
                    } else {
                      onOpenFamilyDigest({
                        id: patient.id,
                        name: patient.fullName,
                        recipientName: patient.familyContact.name,
                        recipientRelation: patient.familyContact.relation,
                        recipientPhone: patient.familyContact.phone,
                        locationOrRoom: patient.bedNumber,
                        sleepScoreOrState: `${baseProfile.sleepTotal} restful sleep`,
                        vitalsSummary: `BP ${patient.vitals.bpSystolic}/${patient.vitals.bpDiastolic} mmHg, HR ${patient.vitals.heartRate} bpm`,
                        nutritionActivitySummary: `${steps.toLocaleString()} steps & ${waterLiters}L hydration logged`
                      });
                    }
                  }}
                  className="w-full p-2.5 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-slate-950 font-extrabold text-[11px] transition flex items-center justify-center gap-1.5 shadow-md"
                >
                  <MessageSquareHeart className="w-3.5 h-3.5" />
                  <span>Share Daily Family SMS Digest</span>
                </button>
              )}

              <div className="pt-1 border-t border-slate-800/80 text-center">
                <span className="text-[9.5px] font-mono text-slate-500 flex items-center justify-center gap-1">
                  <ShieldCheck className="w-3 h-3 text-teal-400" />
                  <span>Synced with MediCore-OS EHR &amp; Family Portal</span>
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Right Column: Gemini 3.8 Wellness Coach Interactive Chat */}
        <div className="lg:col-span-4 bg-slate-900/90 border border-slate-800 rounded-2xl flex flex-col h-[680px] shadow-xl overflow-hidden">
          <div className="p-4 border-b border-slate-800 bg-slate-950/60 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-teal-500/10 text-teal-400 rounded-lg">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-xs font-bold text-slate-100">Gemini 3.8 Preventive Coach</h3>
                <span className="text-[10px] text-teal-400 font-medium">
                  Grounded in Health Connect + Aqara IoT ({baseProfile.name.split(' ')[0]})
                </span>
              </div>
            </div>
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          </div>

          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {chatMessages.map((msg, i) => (
              <div
                key={i}
                className={`p-3 rounded-xl text-xs leading-relaxed max-w-[92%] ${
                  msg.sender === 'ai'
                    ? 'bg-slate-800/70 border border-slate-700/60 text-slate-200 self-start'
                    : 'bg-teal-600 text-white ml-auto font-medium'
                }`}
              >
                {msg.text}
              </div>
            ))}
            {isThinking && (
              <div className="p-3 rounded-xl text-xs bg-slate-800/70 border border-slate-700/60 text-teal-300 italic animate-pulse">
                Synthesizing wearable telemetry, medication schedule &amp; sensor data with Gemini 3.8...
              </div>
            )}
          </div>

          {/* Quick Prompt Chips */}
          <div className="p-2.5 bg-slate-950/40 border-t border-slate-800/80 flex flex-wrap gap-1.5">
            <button
              onClick={() => handleSendMessage(`Suggest anti-inflammatory dinner options for ${baseProfile.name}'s ${baseProfile.diagnosis}`)}
              className="text-[10px] bg-slate-800 hover:bg-slate-700 text-slate-300 px-2 py-1 rounded-md border border-slate-700 transition"
            >
              🥗 Anti-Inflammatory Dinner
            </button>
            <button
              onClick={() => handleSendMessage(`How does today's walking and gait speed support ${baseProfile.name}'s recovery?`)}
              className="text-[10px] bg-slate-800 hover:bg-slate-700 text-slate-300 px-2 py-1 rounded-md border border-slate-700 transition"
            >
              🏃 Mobility &amp; Gait Longevity
            </button>
            <button
              onClick={() => handleSendMessage(`Review medication timing and nocturnal safety for ${baseProfile.name}`)}
              className="text-[10px] bg-slate-800 hover:bg-slate-700 text-slate-300 px-2 py-1 rounded-md border border-slate-700 transition"
            >
              💊 Medication &amp; Sleep Timing
            </button>
          </div>

          {/* Input Bar */}
          <div className="p-3 border-t border-slate-800 bg-slate-950/80 flex gap-2">
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder={`Ask coach about ${baseProfile.name.split(' ')[0]}...`}
              className="flex-1 bg-slate-900 border border-slate-700 rounded-xl px-3 py-1.5 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-teal-500"
            />
            <button
              onClick={() => handleSendMessage()}
              className="p-2 bg-teal-600 hover:bg-teal-500 text-white rounded-xl transition"
            >
              <Send className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
